<template>
  <v-card class="pa-5 main-grid" :loading="isLoading" :disabled="isLoading">
    <v-data-table
      :headers="headers"
      :items="documentModel.translationDocuments"
    >
      <template v-slot:top>
        <v-toolbar flat>
          <v-toolbar-title>Translations</v-toolbar-title>
          <v-spacer></v-spacer>
          <v-dialog v-model="dialog" max-width="800px" persistent>
            <template v-slot:activator="{ on }">
              <v-btn color="primary" v-on="on">New Item</v-btn>
            </template>
            <v-card>
              <v-card-title>{{ formTitle }}</v-card-title>
              <v-card-text>
                <v-form ref="edit">
                  <v-container>
                    <v-row class="">
                      <v-col cols="12" md="9">
                        <v-text-field
                          v-model="editedItem.title"
                          label="Title"
                          :rules="rules.title"
                          counter="100"
                          maxlength="100"
                        ></v-text-field>
                      </v-col>
                      <v-col cols="12" md="3">
                        <language-dropdown
                          v-model="editedItem.language"
                          :rules="rules.language"
                          :language-value="editedItem.language"
                          class="pt-3 mt-1"
                        ></language-dropdown>
                      </v-col>
                    </v-row>
                    <v-row>
                      <v-file-input
                        v-model="editedItem.pdf"
                        :rules="rules.pdf"
                        :loading="fileUploading"
                        class="mb-3 pr-3"
                        truncate-length="60"
                        label="PDF Document"
                        accept=".pdf"
                      ></v-file-input
                    ></v-row>
                  </v-container>
                </v-form>
              </v-card-text>

              <v-card-actions>
                <v-spacer></v-spacer>
                <v-btn color="blue darken-1" text @click="close">
                  Cancel
                </v-btn>
                <v-btn
                  color="blue darken-1"
                  text
                  :disabled="fileUploading"
                  @click="save"
                >
                  Save
                </v-btn>
              </v-card-actions>
            </v-card>
          </v-dialog>
        </v-toolbar>
      </template>
      <!-- bad eslint error, can ignore -->
      <template v-slot:item.action="{ item }">
        <v-icon small class="mr-2" @click="editItem(item)">
          mdi-pencil
        </v-icon>
        <v-icon small @click="deleteItem(item)">
          mdi-delete
        </v-icon>
      </template>
    </v-data-table>
  </v-card>
</template>

<script>
import axios from 'axios';
import languageDropdown from '@/components/filters/languageDropdown';
import { mapMutations } from 'vuex';

export default {
  components: {
    languageDropdown
  },
  props: {
    documentModel: {
      type: Object,
      default() {
        return {
          mci: '',
          productArea: 0,
          brand: 0,
          type: 0
        };
      }
    }
  },
  data() {
    return {
      dialog: false,
      isLoading: false,
      fileUploading: false,
      headers: [
        { text: 'Title', value: 'title' },
        { text: 'PDF File', value: 'fileName' },
        { text: 'Language', value: 'languageNavigation.description' },
        { text: 'Actions', value: 'action', sortable: false }
      ],
      editedIndex: -1,
      editedItem: {
        title: '',
        pdf: null,
        language: ''
      },
      defaultItem: {
        title: '',
        pdf: null,
        language: ''
      },
      rules: {
        title: [v => !!v || 'Title is required'],
        pdf: [v => !!v || 'PDF is required'],
        language: [v => !!v || 'Language is required']
      }
    };
  },
  computed: {
    formTitle() {
      return this.editedIndex === -1 ? 'New Item' : 'Edit Item';
    }
  },
  methods: {
    ...mapMutations('snackbar', ['showSnackbar']),
    editItem(item) {
      this.getPDF(item);
      this.editedIndex = this.documentModel.translationDocuments.indexOf(item);
      this.editedItem = Object.assign({}, item);
      this.dialog = true;
    },
    deleteItem(item) {
      if (confirm('Are you sure you want to delete this item?')) {
        this.isLoading = true;
        this.editedItem = Object.assign({}, item);
        axios
          .delete('/TranslationDocument/' + item.documentId + '/' + item.id)
          .then(() => {
            this.$emit('updated');
            this.showSnackbar({
              text: 'Removed translation',
              color: 'success',
              timeout: 3000
            });
          })
          .catch(error => {
            this.showSnackbar({ text: error.response.data, color: 'error' });
          })
          .finally(() => {
            this.editedItem = Object.assign({}, this.defaultItem);
            this.editedIndex = -1;
            this.isLoading = false;
          });
      }
    },
    getPDF(item) {
      this.fileUploading = true;
      axios
        .get('/DocLibFile/' + item.documentLink)
        .then(response => {
          let blob = new Blob([response.data.filestream], {
            type: 'application/pdf'
          });
          blob['name'] = response.data.name;
          this.editedItem.pdf = blob;
          this.fileUploading = false;
        })
        .catch(error => {
          this.showSnackbar({ text: error, color: 'error' });
          this.fileUploading = false;
        });
    },
    close() {
      this.dialog = false;
      this.editedItem = Object.assign({}, this.defaultItem);
      this.editedIndex = -1;
      this.$refs.edit.reset();
    },
    save() {
      if (this.$refs.edit.validate()) {
        this.isLoading = true;
        this.fileUploading = true;
        let formData = new FormData();
        let method = this.editedIndex > -1 ? 'put' : 'post';
        let url = '/TranslationDocument';

        if (this.editedIndex > -1) {
          //edit existing
          url =
            '/TranslationDocument/' +
            this.editedItem.documentId +
            '/' +
            this.editedItem.id;
          formData.append('documentLink', this.editedItem.documentLink);
        } else {
          //create new;
          url = '/TranslationDocument';
          let existingTrans = this.documentModel.translationDocuments.find(
            x => x.language === this.editedItem.language
          );
          if (existingTrans) {
            this.showSnackbar({
              text: `${existingTrans.languageNavigation.description} translation already exists`,
              color: 'error'
            });
            return;
          }
        }

        formData.append('title', this.editedItem.title);
        formData.append('language', this.editedItem.language);
        formData.append('file', this.editedItem.pdf);
        formData.append('documentId', this.documentModel.id);

        axios({
          method: method,
          url: url,
          data: formData,
          headers: { 'Content-Type': 'multipart/form-data' }
        })
          .then(() => {
            this.$emit('updated');
            this.close();
            this.isLoading = false;
            this.fileUploading = false;
          })
          .catch(error => {
            this.showSnackbar({ text: error, color: 'error' });
            this.isLoading = false;
            this.fileUploading = false;
          });
      }
    }
  }
};
</script>

<style scoped>
.main-grid {
  min-width: 100%;
}
</style>
